import { any, filter, find, length, propEq, propOr } from 'ramda';
import { isNotNilOrEmpty } from '@@serverHelpers/index.mjs';

/**
 * Retourne true si le niveau (pricing) possède une entrée disponible pour la structure tarifaire donnée.
 *
 * Règles :
 * - Entrée absente dans totalPrices → indisponible (pas de tarif pour cette structure).
 * - Entrée présente sans champ isAvailable (ancien format / mono-zone) → disponible.
 * - Entrée présente avec isAvailable === false → indisponible.
 * - Toute autre valeur de isAvailable (true, undefined, null…) → disponible.
 */
const isLevelAvailableFor = (tariffStructureCode) => (pricing) => {
  const entry = find(
    propEq('tariffStructureCode', tariffStructureCode),
    pricing.totalPrices || []
  );
  if (!entry) return false;
  return entry.isAvailable !== false;
};

// Colonne de comparaison préfixée par le serveur : elle ne porte aucun tarif (pas de totalPrices)
const MINIMUM_CCN_LABEL = 'Minimum CCN';
const isMinimumCcnPricing = propEq('pricingLabel', MINIMUM_CCN_LABEL);

/**
 * Filtre les niveaux (pricings) en ne gardant que ceux disponibles pour la structure tarifaire donnée.
 * La colonne Minimum CCN n'étant pas tarifée, elle est conservée tant qu'au moins un niveau
 * réel reste disponible (sinon la grille est vide et displayErrorPrices prend le relais).
 */
const filterAvailablePricings = (tariffStructureCode, pricings) => {
  const isAvailable = isLevelAvailableFor(tariffStructureCode);
  const hasAvailableLevel = any(
    (pricing) => !isMinimumCcnPricing(pricing) && isAvailable(pricing),
    pricings
  );

  return hasAvailableLevel
    ? filter(
      (pricing) => isMinimumCcnPricing(pricing) || isAvailable(pricing),
      pricings
    )
    : [];
};

/**
 * Filtre les structures tarifaires sélectionnables : ne garde que celles pour lesquelles
 * au moins un niveau est disponible. Évite de proposer une structure qui afficherait une grille vide.
 */
const filterSelectableTariffStructures = (tariffStructures, pricings) =>
  filter(
    (ts) => isNotNilOrEmpty(filterAvailablePricings(ts.value, pricings)),
    tariffStructures
  );

/**
 * Zones de couverture où le niveau est éligible pour la structure tarifaire
 */
const getEligibleZones = (tariffStructureCode) => (pricing) => {
  const entry = find(
    propEq('tariffStructureCode', tariffStructureCode),
    pricing.totalPrices || []
  );
  const eligibleZones = propOr([], 'eligibleZones', entry);
  const zoneCount =
    length(eligibleZones) + length(propOr([], 'unavailableZones', entry));

  return zoneCount > 1 ? eligibleZones : [];
};

export {
  isLevelAvailableFor,
  filterAvailablePricings,
  filterSelectableTariffStructures,
  getEligibleZones
};
